import type { FC } from 'react'
import { useEffect, useMemo, useState } from 'react'

import { LoaderCircle, MessagePopup, Tooltip } from '@skytech/ui'
import { StyledText } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { Container } from '@skytech/ui/Container'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import AlertDecagramIcon from 'mdi-react/AlertDecagramIcon'
import CheckDecagramIcon from 'mdi-react/CheckDecagramIcon'
import { useNavigate, useParams } from 'react-router-dom'
import { twJoin } from 'tailwind-merge'

import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import { LoadingError } from '@/components/LoadingError'
import Tabs from '@/components/Tabs'
import { useAdminAccess } from '@/hooks/access'
import { useSearchParamsByKey } from '@/hooks/searchParams'
import { useLocalDate } from '@/hooks/use-local-date'
import queries from '@/modules/admin/queries'
import adminResources from '@/modules/admin/resources'
import { APP_ROUTES } from '@/util/app-routes'

import { Assets } from './Assets'
import PolicySubscriptions from './PolicySubscriptions'
import Profile from './Profile'

import type { CostObjectDetails as CostObjectDetailsType } from '../../resources'

import './index.styles.scss'

export const CostObjectDetails: FC = () => {
  useAdminAccess()

  const navigate = useNavigate()
  const translate = useTranslation()
  const params = useParams()

  const costObjectId = Number(params.costObjectId)

  if (isNaN(costObjectId)) throw Error(translate('Employee ID is not a number!'))

  const { data, isError } = useQuery(queries.adminCostObjectDetails(costObjectId), () =>
    adminResources.getCostObjectDetails(costObjectId)
  )

  const { data: assets, isLoading: isAssetsLoading } = useQuery(
    queries.adminAssetList(),
    adminResources.getAssetList
  )
  const employeeAssets = assets?.filter(asset => asset.costObjectId === costObjectId)

  const costObjectDetailsTabs = useMemo(
    () => [
      { value: 'profile', label: <Translation id="Profile" /> },
      {
        value: 'policy-subs',
        label: (
          <>
            <Translation id="Policy subscriptions" />
            {'  '}
            {data?.ecomPolicySubscriptions?.length === 0 ? (
              <StyledText size="regular-plus" colorMode="gray" className="count-styling">
                {data?.ecomPolicySubscriptions?.length}
              </StyledText>
            ) : (
              <StyledText size="regular-plus" className="count-styling">
                {data?.ecomPolicySubscriptions?.length}
              </StyledText>
            )}
          </>
        ),
      },
      {
        value: 'assets',
        label: (
          <>
            {<Translation id="Assets" />}{' '}
            {isAssetsLoading ? (
              <Translation id="Loading..." />
            ) : (
              <>
                {employeeAssets?.length === 0 ? (
                  <StyledText size="regular-plus" colorMode="gray" className="count-styling">
                    {employeeAssets?.length}
                  </StyledText>
                ) : (
                  <StyledText size="regular-plus" className="count-styling">
                    {employeeAssets?.length}
                  </StyledText>
                )}
              </>
            )}
          </>
        ),
      },
    ],
    [data?.ecomPolicySubscriptions?.length, employeeAssets?.length, isAssetsLoading]
  )

  const [queryTab, setQueryTab] = useSearchParamsByKey('tab')
  const [tabFilter, setTabFilter] = useState(queryTab || costObjectDetailsTabs[0].value)

  useEffect(() => {
    setTabFilter(queryTab || costObjectDetailsTabs[0].value)
  }, [costObjectDetailsTabs, queryTab])

  if (isError)
    return (
      <Container onSwipedLeft={() => navigate(APP_ROUTES.ADMIN_COST_OBJECTS)}>
        <LoadingError />
      </Container>
    )

  if (!data) {
    return (
      <Container onSwipedLeft={() => navigate(APP_ROUTES.ADMIN_COST_OBJECTS)}>
        <LoaderCircle />
      </Container>
    )
  }

  return (
    <Container onSwipedLeft={() => navigate(APP_ROUTES.ADMIN_COST_OBJECTS)}>
      <div className="employee-details-title">
        <div className="flex items-center">
          <Container.Header>{data.name}</Container.Header>{' '}
          {data.type === 'E' && (
            <span className="mx-1">
              (<Translation id="Private number" />)
            </span>
          )}
          {data?.consent && <ConsentIndicator costObject={data} />}
        </div>
        <h4 className="customer-name">{data.customerName}</h4>
      </div>

      <Tabs
        selectedValue={tabFilter}
        tabs={costObjectDetailsTabs}
        onClick={tabId => {
          setTabFilter(String(tabId))
          setQueryTab(String(tabId))
        }}
      />

      <div className="cost-object-details">
        {tabFilter === 'profile' && (
          <Profile
            costObjectDetails={data}
            onGoToAssetTab={() => {
              setTabFilter('assets')
              setQueryTab('assets')
            }}
          />
        )}
        {tabFilter === 'policy-subs' && <PolicySubscriptions />}
        {tabFilter === 'assets' && <Assets />}
      </div>
    </Container>
  )
}

const ConsentIndicator: FC<{ costObject: CostObjectDetailsType }> = ({ costObject }) => {
  const translate = useTranslation()
  const { formatDate } = useLocalDate()
  const queryClient = useQueryClient()
  const [isPopupOpen, setIsPopupOpen] = useState(false)

  const revokeConsentMutation = useMutation(adminResources.revokeConsent, {
    onSuccess() {
      queryClient.invalidateQueries(queries.adminCostObjectDetails(costObject.id))
      queryClient.invalidateQueries(queries.adminConsents())
      setIsPopupOpen(false)
    },
  })

  const tooltipContent =
    costObject.consent?.isConsentGiven && costObject.consent.consentGivenDate
      ? translate('Consent given at {date}', {
          date: formatDate({ date: costObject.consent.consentGivenDate }),
        })
      : translate('Consent missing')

  return (
    <>
      <Button
        className={twJoin('m-0 p-0', !costObject.consent?.isConsentGiven && 'pointer-events-none')}
        id="consent-indicator"
        variant="tertiary"
        onClick={() => setIsPopupOpen(true)}
      >
        {costObject.consent?.isConsentGiven ? (
          <CheckDecagramIcon className="fill-green-500" />
        ) : (
          <AlertDecagramIcon className="fill-red-500" />
        )}

        <Tooltip anchorId="consent-indicator" content={tooltipContent} size="large" place="right" />
      </Button>

      <MessagePopup
        variant="narrow"
        type="success"
        title={<Translation id="Employee consent" />}
        message={
          <Translation id="Removing consent forces user to agree to the given terms on their next login" />
        }
        isOpen={isPopupOpen}
        onClose={() => setIsPopupOpen(false)}
      >
        <footer className="mt-2 flex items-center justify-end">
          <Button className="mb-0" variant="tertiary" onClick={() => setIsPopupOpen(false)}>
            <Translation id="Cancel" />
          </Button>

          <Button
            className="mb-0"
            isLoading={revokeConsentMutation.isLoading}
            onClick={() => {
              if (!costObject.consent) throw Error('No consent found!')
              revokeConsentMutation.mutate({
                consentId: costObject.consent.id,
                costObjectId: costObject.id,
              })
            }}
          >
            <Translation id="Revoke consent" />
          </Button>
        </footer>
      </MessagePopup>
    </>
  )
}
